if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.applyInitialColorFilter();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          this.cacheAllSlides();

          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        if (!this._zoomDelegateAdded) {
          this._zoomDelegateAdded = true;
          FoxTheme.utils.addEventDelegate({
            selector: '.js-photoswipe--zoom',
            context: this,
            handler: (e, media) => {
              if (media.dataset.mediaType === 'image') {
                const index = Number(media.dataset.mediaIndex) || 0;
                this.lightbox.loadAndOpen(index);
              }
            },
          });
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        const colorTag = this.getColorTagFromVariant(variant);
        const sizeTag = this.getSizeTagFromVariant(variant);
        const paddingTag = this.getPaddingTagFromVariant(variant);
        const didFilter = colorTag !== null || sizeTag !== null || paddingTag !== null;

        if (didFilter) {
          this.filterMedia(colorTag, sizeTag, paddingTag);
        } else {
          this.querySelectorAll('[data-default-hidden]').forEach((slide) =>
            slide.removeAttribute('data-default-hidden')
          );
        }

        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        const navigate = () => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const slides = [
              ...this.elements.mediaList.querySelectorAll(
                '.product__media-item:not(.swiper-slide-duplicate)'
              ),
            ];
            const idx = slides.findIndex((s) => s.dataset.mediaId == variant.featured_media.id);
            this.sliderInstance.slider.slideToLoop(idx !== -1 ? idx : 0);
          } else {
            this.sortMediaItems(variant);
          }
        };

        // If we just rebuilt the slider via filterMedia, defer one frame so Swiper is ready
        didFilter ? requestAnimationFrame(navigate) : navigate();
      }

      // ── Shared helpers ─────────────────────────────────────────────────────

      cacheAllSlides() {
        if (this._allMainSlides) return;
        this._allMainSlides = [
          ...this.elements.mediaList.querySelectorAll('.product__media-item'),
        ];
        const thumbsWrapper = this.elements.thumbnails?.querySelector('.swiper-wrapper');
        this._allThumbSlides = thumbsWrapper
          ? [...thumbsWrapper.querySelectorAll('.product__thumbs-item')]
          : [];
      }

      // Slugifies an option value the same way the alt tags are written:
      // "Pearl Grey" → "pearl-grey", "Grün" → "gruen", "pearl-grey" → "pearl-grey"
      normalizeTag(value) {
        return String(value ?? '')
          .toLowerCase()
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss')
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      // ── Color-filter helpers ───────────────────────────────────────────────

      // Reads "#color-red" / "#color-pearl-grey" directly from the image alt,
      // independent of any other tags (size, polsterung, …) in the same alt.
      // Falls back to data-color-tag (set by Liquid) if the alt has no color
      // token. Result is cached on the element.
      getColorTagFromElement(el) {
        if (el.dataset.colorTagParsed !== undefined) {
          return el.dataset.colorTagParsed === '' ? null : el.dataset.colorTagParsed;
        }

        const alt = el.querySelector('img')?.getAttribute('alt') || '';
        const match = alt.match(/(?:^|[\s;#_-])color-([^\s;#,]+)/i);
        let color = match ? this.normalizeTag(match[1]) : null;

        if (!color && el.dataset.colorTag) {
          color = this.normalizeTag(el.dataset.colorTag) || null;
        }

        el.dataset.colorTagParsed = color || '';
        return color;
      }

      // Built from the cached full slide list so filtered-out slides still count.
      getKnownColorTags() {
        if (!this._knownColorTags) {
          this.cacheAllSlides();
          this._knownColorTags = new Set(
            this._allMainSlides
              .map((el) => this.getColorTagFromElement(el))
              .filter((tag) => tag && tag !== 'all')
          );
        }
        return this._knownColorTags;
      }

      getColorTagFromVariant(variant) {
        if (!variant) return null;
        const known = this.getKnownColorTags();
        if (!known.size) return null;

        // 1) Option value matches a color tag directly ("Pearl Grey" → "pearl-grey")
        for (const opt of variant.options || []) {
          const tag = this.normalizeTag(opt);
          if (tag && known.has(tag)) return tag;
        }

        // 2) Fallback: color tag of the variant's featured image. Works even if
        //    option value and tag differ ("Graphit Grau" vs. "graphit-grey").
        const mediaId = variant.featured_media?.id;
        if (mediaId) {
          this.cacheAllSlides();
          const id = String(mediaId);
          const slide = this._allMainSlides.find((s) => {
            const sid = String(s.dataset.mediaId || '');
            return sid === id || sid.endsWith('-' + id);
          });
          const tag = slide ? this.getColorTagFromElement(slide) : null;
          if (tag && tag !== 'all') return tag;
        }

        return null;
      }

      // ── Size-filter helpers ────────────────────────────────────────────────

      // "62.0", "62,0", "62 cm", 62 → "62"
      normalizeSize(raw) {
        if (raw === null || raw === undefined || raw === '') return null;
        const n = parseFloat(String(raw).replace(',', '.'));
        return Number.isNaN(n) ? null : String(n);
      }

      // variant.id → seat height, from the JSON rendered by Liquid
      getVariantSizeMap() {
        if (this._variantSizeMap) return this._variantSizeMap;

        this._variantSizeMap = {};
        const el =
          this.querySelector('[data-variant-sizes]') ||
          document.querySelector('[data-variant-sizes]');

        if (el) {
          try {
            const raw = JSON.parse(el.textContent);
            Object.keys(raw).forEach((id) => {
              const size = this.normalizeSize(raw[id]);
              if (size) this._variantSizeMap[id] = size;
            });
          } catch (_) {}
        }
        return this._variantSizeMap;
      }

      getSizeTagFromVariant(variant) {
        if (!variant?.id) return null;
        return this.getVariantSizeMap()[variant.id] || null;
      }

      // Reads "size-54-cm" / "#size-62" from the image alt. Result is cached
      // on the element. Media without a size token returns null and is never
      // filtered out by size.
      getSizeTagFromElement(el) {
        if (el.dataset.sizeTag !== undefined) {
          return el.dataset.sizeTag === '' ? null : el.dataset.sizeTag;
        }

        const alt = el.querySelector('img')?.getAttribute('alt') || '';
        const match = alt.match(/(?:^|[\s;#_-])size-(\d+(?:[.,]\d+)?)/i);
        const size = match ? this.normalizeSize(match[1]) : null;

        el.dataset.sizeTag = size || '';
        return size;
      }

      // ── Padding-filter helpers (Polsterung) ────────────────────────────────

      // Reads "#polsterung-soft" / "#polsterung-fest" from the image alt.
      // Result is cached on the element. Media without a padding token
      // returns null and is never filtered out by padding.
      getPaddingTagFromElement(el) {
        if (el.dataset.paddingTag !== undefined) {
          return el.dataset.paddingTag === '' ? null : el.dataset.paddingTag;
        }

        const alt = el.querySelector('img')?.getAttribute('alt') || '';
        const match = alt.match(/(?:^|[\s;#_-])polsterung-([^\s;#,]+)/i);
        const padding = match ? this.normalizeTag(match[1]) : null;

        el.dataset.paddingTag = padding || '';
        return padding;
      }

      // All padding values that occur on at least one media item, e.g. {soft, fest}.
      // Built from the cached full slide list so filtered-out slides still count.
      getKnownPaddingTags() {
        if (!this._knownPaddingTags) {
          this.cacheAllSlides();
          this._knownPaddingTags = new Set(
            this._allMainSlides
              .map((el) => this.getPaddingTagFromElement(el))
              .filter((tag) => tag && tag !== 'all')
          );
        }
        return this._knownPaddingTags;
      }

      // Matches the variant's option values ("Soft", "Fest") against the
      // padding values found in the alt tags.
      getPaddingTagFromVariant(variant) {
        if (!variant?.options) return null;
        const known = this.getKnownPaddingTags();
        if (!known.size) return null;

        for (const opt of variant.options) {
          const tag = this.normalizeTag(opt);
          if (tag && known.has(tag)) return tag;
        }
        return null;
      }

      // ── Filtering ──────────────────────────────────────────────────────────

      // A slide is shown when ALL conditions hold. A missing tag on the
      // slide means "don't care" for that dimension:
      //   #color-red;#size-54;#polsterung-fest → only red AND 54 AND fest
      //   #color-red;#size-54                  → only red AND 54, any padding
      //   #color-red                           → only red, any size/padding
      //   #polsterung-soft                     → only soft, any color/size
      //   (no tag)                             → always visible
      filterMedia(colorTag, sizeTag = null, paddingTag = null) {
        this.cacheAllSlides();

        // Liquid hides slides outside the default variant until this filter has run
        this._allMainSlides.forEach((slide) => slide.removeAttribute('data-default-hidden'));

        const matches = (el) => {
          const color = this.getColorTagFromElement(el);
          const colorOk = !colorTag || !color || color === 'all' || color === colorTag;
          if (!colorOk) return false;

          const size = this.getSizeTagFromElement(el);
          const sizeOk = !sizeTag || !size || size === sizeTag;
          if (!sizeOk) return false;

          const padding = this.getPaddingTagFromElement(el);
          return !paddingTag || !padding || padding === 'all' || padding === paddingTag;
        };

        // Destroy existing Swiper instances
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }

        // Rebuild main slider wrapper with only matching slides
        this.elements.mediaList.innerHTML = '';
        this._allMainSlides.filter(matches).forEach((slide, i) => {
          slide.className = slide.className.replace(/\bswiper-slide-\S+/g, '').trim();
          // Keep data-media-index in sync with the filtered array index for lightbox
          slide.dataset.mediaIndex = i;
          const zoomBtn = slide.querySelector('[data-media-index]');
          if (zoomBtn) zoomBtn.dataset.mediaIndex = i;
          this.elements.mediaList.appendChild(slide);
        });

        // Rebuild thumbs wrapper
        const thumbsWrapper = this.elements.thumbnails?.querySelector('.swiper-wrapper');
        if (thumbsWrapper) {
          thumbsWrapper.innerHTML = '';
          this._allThumbSlides.filter(matches).forEach((slide) => {
            slide.className = slide.className.replace(/\bswiper-slide-\S+/g, '').trim();
            thumbsWrapper.appendChild(slide);
          });
        }

        // Re-initialise Swiper
        this.updateMediaLayout();

        // Re-initialise lightbox so its dataSource reflects the filtered set
        if (this.enableImageZoom) {
          if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
          }
          this.initImageZoom();
        }
      }

      // Kept for backwards compatibility with existing call sites.
      filterByColor(colorTag) {
        this.filterMedia(colorTag, null, null);
      }

      applyInitialColorFilter() {
        const variantEl = document.querySelector('[data-selected-variant]');
        if (!variantEl) return;
        try {
          const variant = JSON.parse(variantEl.textContent);
          const colorTag = this.getColorTagFromVariant(variant);
          const sizeTag = this.getSizeTagFromVariant(variant);
          const paddingTag = this.getPaddingTagFromVariant(variant);
          if (colorTag || sizeTag || paddingTag) this.filterMedia(colorTag, sizeTag, paddingTag);
        } catch (_) {}
      }

      // ── End color/size/padding-filter helpers ──────────────────────────────

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}